import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { fromUTCToSaoPaulo, toSupabaseDate } from '../utils/dateUtils';
import AppIcon from './icons/AppIcon';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Agendado', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  { value: 'sent', label: 'Enviado', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' },
  { value: 'canceled', label: 'Cancelado', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
  { value: 'failed', label: 'Falhou', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' }
];

export default function CRMAppointmentModal({ event, onClose, onSave, onDelete, onOpenContact }) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    status: 'scheduled'
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (event) {
      const start = event.start_date ? fromUTCToSaoPaulo(new Date(event.start_date)) : null;
      const end = event.end_date ? fromUTCToSaoPaulo(new Date(event.end_date)) : null;
      
      setEditData({
        title: event.title || '',
        description: event.description || '',
        startDate: start ? start.toISOString().split('T')[0] : '',
        startTime: start ? start.toTimeString().slice(0, 5) : '',
        endDate: end ? end.toISOString().split('T')[0] : '',
        endTime: end ? end.toTimeString().slice(0, 5) : '',
        status: event.status || 'scheduled',
        formData: event.custom_fields?.form_fields?.form_data || {}
      });

      if (event.contact_id) {
        loadContactData(event.contact_id);
      } else {
        setLoading(false);
      }
    }
  }, [event]);

  const loadContactData = async (contactId) => {
    if (!contactId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('contatos')
        .select('*')
        .eq('id', contactId)
        .single();

      if (!error && data) {
        setContact(data);
      }
    } catch (err) {
      console.error('Erro ao carregar contato:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 12) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
    }
    return phone;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSave = async () => {
    if (!editData.title.trim()) {
      alert('O título é obrigatório');
      return;
    }

    setSaving(true);

    try {
      const startDateTime = toSupabaseDate(new Date(`${editData.startDate}T${editData.startTime || '00:00'}`));
      const endDateTime = editData.endDate 
        ? toSupabaseDate(new Date(`${editData.endDate}T${editData.endTime || '23:59'}`))
        : startDateTime;

      const updates = {
        title: editData.title,
        description: editData.description || null,
        start_date: startDateTime,
        end_date: endDateTime,
        status: editData.status,
        custom_fields: {
          ...event.custom_fields,
          form_fields: {
            ...event.custom_fields?.form_fields,
            form_data: editData.formData || {}
          }
        },
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('calendar_events')
        .update(updates)
        .eq('id', event.id);

      if (error) throw error;

      if (onSave) {
        onSave({ ...event, ...updates });
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error('Erro ao salvar:', err);
      alert('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSaving(true);

    try {
      const { error } = await supabase
        .from('calendar_events')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id);

      if (error) throw error;

      if (onSave) {
        onSave({ ...event, status: newStatus });
      }
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      alert('Erro ao alterar status');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      const currentAttachments = event.attachments || [];
      const newAttachments = [...currentAttachments];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = `${Date.now()}_${file.name}`;
        const filePath = `calendar-events/${event.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('files')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Erro ao fazer upload:', uploadError);
          continue;
        }

        const { data: urlData } = supabase.storage
          .from('files')
          .getPublicUrl(filePath);

        newAttachments.push({
          nome: file.name,
          url: urlData.publicUrl,
          tipo: file.type,
          tamanho: file.size,
          data_upload: new Date().toISOString()
        });
      }

      const { error } = await supabase
        .from('calendar_events')
        .update({ 
          attachments: newAttachments,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id);

      if (error) throw error;

      if (onSave) {
        onSave({ ...event, attachments: newAttachments });
      }

      alert(`${files.length} arquivo(s) anexado(s) com sucesso!`);
    } catch (err) {
      console.error('Erro ao anexar arquivos:', err);
      alert('Erro ao anexar arquivos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = async (index) => {
    if (!confirm('Remover este arquivo?')) return;

    try {
      const currentAttachments = event.attachments || [];
      const newAttachments = currentAttachments.filter((_, i) => i !== index);

      const { error } = await supabase
        .from('calendar_events')
        .update({ 
          attachments: newAttachments,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id);

      if (error) throw error;

      if (onSave) {
        onSave({ ...event, attachments: newAttachments });
      }
    } catch (err) {
      console.error('Erro ao remover arquivo:', err);
      alert('Erro ao remover arquivo');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir este agendamento?')) return;

    try {
      const { error } = await supabase
        .from('calendar_events')
        .update({ 
          status: 'canceled',
          is_deleted: true,
          deleted_at: new Date().toISOString()
        })
        .eq('id', event.id);

      if (error) throw error;

      if (onDelete) {
        onDelete(event.id);
      }
      
      onClose();
    } catch (err) {
      console.error('Erro ao excluir:', err);
      alert('Erro ao excluir agendamento');
    }
  };

  if (!event) return null;

  const startDate = event.start_date ? fromUTCToSaoPaulo(new Date(event.start_date)) : null;
  const endDate = event.end_date ? fromUTCToSaoPaulo(new Date(event.end_date)) : null;
  const currentStatus = STATUS_OPTIONS.find(s => s.value === (isEditing ? editData.status : event.status)) || STATUS_OPTIONS[0];

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '2rem'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'rgba(15, 12, 41, 0.98)',
          border: '1px solid rgba(180, 59, 234, 0.3)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '1.5rem 2rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'var(--primary-color)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
            <AppIcon name="event" style={{ fontSize: '2rem', color: '#44e1ff' }} />
            <div style={{ flex: 1 }}>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  padding: '0.5rem',
                  color: '#fff',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  width: '100%'
                }}
                placeholder="Título do evento"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                  {startDate && `${formatDate(startDate.toISOString())} • ${formatTime(startDate.toISOString())}${endDate ? ' - ' + formatTime(endDate.toISOString()) : ''}`}
                </p>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: currentStatus.color,
                  background: currentStatus.bg,
                  border: `1px solid ${currentStatus.color}40`
                }}>
                  {currentStatus.label}
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              <AppIcon name="close" style={{ fontSize: '1.25rem' }} />
            </button>
          </div>
        </div>

        {/* Status Actions */}
        <div style={{ margin: '1rem 1.5rem 0', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {event.status === 'scheduled' && (
            <>
              <button onClick={() => handleStatusChange('sent')} disabled={saving} style={{ padding: '0.4rem 0.75rem', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '6px', color: '#10b981', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <AppIcon name="check_circle" style={{ fontSize: '1rem' }} /> Confirmar
              </button>
              <button onClick={() => setShowReschedule(true)} disabled={saving} style={{ padding: '0.4rem 0.75rem', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '6px', color: '#f59e0b', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <AppIcon name="schedule" style={{ fontSize: '1rem' }} /> Remarcar
              </button>
              <button onClick={() => handleStatusChange('canceled')} disabled={saving} style={{ padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', color: '#ef4444', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <AppIcon name="cancel" style={{ fontSize: '1rem' }} /> Cancelar
              </button>
            </>
          )}
        </div>

        {/* Reschedule Form */}
        {showReschedule && (
          <div style={{ margin: '0 1.5rem 1rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '12px', padding: '1rem' }}>
            <h4 style={{ color: '#f59e0b', margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AppIcon name="schedule" style={{ fontSize: '1rem' }} /> Remarcar Agendamento
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ color: '#9bb3c7', fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Nova Data</label>
                <input type="date" value={editData.startDate} onChange={(e) => setEditData({ ...editData, startDate: e.target.value })} style={{ width: '100%', padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff' }} />
              </div>
              <div>
                <label style={{ color: '#9bb3c7', fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Novo Horário</label>
                <input type="time" value={editData.startTime} onChange={(e) => setEditData({ ...editData, startTime: e.target.value })} style={{ width: '100%', padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setShowReschedule(false)} style={{ padding: '0.4rem 0.75rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '0.4rem 0.75rem', background: 'rgba(245, 158, 11, 0.2)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '6px', color: '#f59e0b', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>{saving ? 'Salvando...' : 'Salvar Nova Data'}</button>
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '1.5rem 2rem', overflowY: 'auto', flex: 1 }}>
          {/* Contact Info */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ 
              color: '#44e1ff', 
              fontSize: '0.85rem', 
              fontWeight: 600, 
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <AppIcon name="person" style={{ fontSize: '1rem' }} />
              INFORMAÇÕES DO CONTATO
            </h3>
            
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.03)', 
              borderRadius: '12px', 
              padding: '1rem',
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              {loading ? (
                <p style={{ color: '#9bb3c7', margin: 0 }}>Carregando...</p>
              ) : contact ? (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ color: '#9bb3c7', fontSize: '0.75rem', margin: '0 0 0.25rem' }}>Nome</p>
                      <p style={{ color: '#fff', fontSize: '1rem', margin: 0, fontWeight: 500 }}>{contact.nome}</p>
                    </div>
                    {onOpenContact && (
                      <button 
                        onClick={() => onOpenContact(contact.id)}
                        style={{
                          padding: '0.4rem 0.75rem',
                          background: 'rgba(68, 225, 255, 0.1)',
                          border: '1px solid rgba(68, 225, 255, 0.3)',
                          borderRadius: '6px',
                          color: '#44e1ff',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <AppIcon name="open_in_new" style={{ fontSize: '1rem' }} />
                        Ver perfil
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <p style={{ color: '#9bb3c7', fontSize: '0.75rem', margin: '0 0 0.25rem' }}>Telefone</p>
                      <p style={{ color: '#fff', fontSize: '0.9rem', margin: 0 }}>{formatPhoneNumber(contact.numero)}</p>
                    </div>
                    <div>
                      <p style={{ color: '#9bb3c7', fontSize: '0.75rem', margin: '0 0 0.25rem' }}>Email</p>
                      <p style={{ color: '#fff', fontSize: '0.9rem', margin: 0 }}>{contact.email || '-'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ color: '#9bb3c7', margin: 0 }}>Contato não encontrado</p>
              )}
            </div>
          </div>

          {/* Form Data */}
          {event?.custom_fields?.form_fields?.form_data && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                color: '#44e1ff', 
                fontSize: '0.85rem', 
                fontWeight: 600, 
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AppIcon name="description" style={{ fontSize: '1rem' }} />
                DADOS DO FORMULÁRIO
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '0.75rem' 
              }}>
                {Object.entries(event.custom_fields.form_fields.form_data).map(([key, value]) => {
                  // Formatar data/hora se for campo de data
                  let displayValue = value;
                  if (key.includes('data') || key.includes('date') || key.includes('Data')) {
                    try {
                      const dateValue = new Date(value);
                      if (!isNaN(dateValue.getTime())) {
                        displayValue = dateValue.toLocaleDateString('pt-BR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      }
                    } catch (e) {
                      // mantém o valor original
                    }
                  }
                  
                  return (
                    <div key={key} style={{ 
                      background: 'rgba(255, 255, 255, 0.03)', 
                      borderRadius: '8px', 
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.05)'
                    }}>
                      <p style={{ color: '#9bb3c7', fontSize: '0.7rem', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>
                        {key.replace(/_/g, ' ')}
                      </p>
                      <input
                        type="text"
                        value={editData.formData?.[key] ?? displayValue}
                        onChange={(e) => setEditData({ 
                          ...editData, 
                          formData: { ...editData.formData, [key]: e.target.value }
                        })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          color: '#fff',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Description / Observações */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ color: '#44e1ff', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AppIcon name="notes" style={{ fontSize: '1rem' }} /> OBSERVAÇÕES
            </h3>
            <textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} placeholder="Adicione observações sobre o agendamento..." style={{ width: '100%', minHeight: '100px', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', resize: 'vertical' }} />
          </div>

          {/* Anexos */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ color: '#44e1ff', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AppIcon name="attach_file" style={{ fontSize: '1rem' }} /> ANEXOS
            </h3>
            {event.attachments && event.attachments.length > 0 && (
              <div style={{ marginBottom: '1rem', display: 'grid', gap: '0.5rem' }}>
                {event.attachments.map((attachment, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, overflow: 'hidden' }}>
                      <AppIcon name={attachment.tipo?.includes('image') ? 'image' : 'description'} style={{ fontSize: '1.25rem', color: '#44e1ff' }} />
                      <a href={attachment.url} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachment.nome}</a>
                    </div>
                    <button onClick={() => handleRemoveAttachment(index)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}>
                      <AppIcon name="delete" style={{ fontSize: '1rem' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ padding: '0.5rem 1rem', background: 'rgba(68, 225, 255, 0.1)', border: '1px solid rgba(68, 225, 255, 0.3)', borderRadius: '6px', color: '#44e1ff', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AppIcon name="upload" style={{ fontSize: '1rem' }} /> {uploading ? 'Enviando...' : 'Adicionar Arquivo'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 2rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={handleDelete} style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AppIcon name="delete" style={{ fontSize: '1rem' }} /> Excluir
          </button>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={onClose} style={{ padding: '0.5rem 1rem', background: 'transparent', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>Fechar</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1rem', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AppIcon name="save" style={{ fontSize: '1rem' }} /> {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
